import {
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import type { PointhomeDashboardData } from '../../types/pointhome.types';

// 금액 포맷터
const formatCurrency = (value: number) => `\u20A9${value.toLocaleString()}`;

type PointhomeDashboardProps = {
  data: PointhomeDashboardData | null;
  selectedApp?: string | null;
};

// 레이블 맵
// const PublisherType: Record<string, string> = {
//   revenueCPC: 'CPC',
//   revenueNews: '뉴스',
//   revenueExternalEarn: '외부 요청 적립',
//   revenueOfferwall: '오퍼월',
//   revenueNasmedia: '나스미디어',
//   revenueDoYouAd: '두유애드',
//   revenueMezzoMedia: '메조미디어',
//   revenueAdPopcorn: '애드팝콘',
//   revenueGoogleAdsense: '구글 애드센스',
//   revenueBuzzvil: '버즈빌',
//   revenueFortune: 'RC 운세/포춘',
//   revenueCommon: 'RC 상식',
//   revenueTencube: 'RC 텐큐브',
//   revenueRoulette: 'RC 룰렛',
//   revenueQuiz: '아바티 퀴즈',
//   revenueWebtoon: '아바티 웹툰',
//   revenueRCNews: 'RC 뉴스',
// };

// const serviceType: Record<string, string> = {
//   pointhome: '포인트홈',
//   gameportal: '게임포탈',
//   rc: 'RC',
//   adcash: '애드캐시',
//   cashroulette: '캐시룰렛',
//   zzal: '짤',
//   Unknown: '기타',
//   haruWeather: '하루날씨',
//   memoG: '메모G',
//   nam: 'NAM'
// };

export default function PointhomeDashboard({ data }: PointhomeDashboardProps) {
  // const [selectedService, setSelectedService] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // sm 이하: 모바일

  if (!data) return null;

  const {
    totalRevenue,
    totalExpense,
    totalProfit,
    // serviceRevenue,
    dailyRevenue
  } = data;

  const chartData = dailyRevenue.map((item) => ({
    date: item.date,
    revenue: item.Revenue,
  }));

  // const handleServiceClick = (serviceKey: string) => {
  //   setSelectedService((prev) => (prev === serviceKey ? null : serviceKey));
  // };

  return (
    <Box sx={{ opacity: 1, transition: 'opacity 0.3s ease' }}>
      {/* 상단: 총 매출 카드 + (데스크톱에서만) 주간 차트 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,2fr)',
          gap: { xs: 1.5, md: 2 }, // 데스크톱에서 카드 간격 더 붙임
          mb: 1,
        }}
      >
        {/* 총 매출 카드 */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            minHeight: 100,
            minWidth: 0,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}
            >
              총 매출
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="#0044D2" sx={{ letterSpacing: '-0.02em' }}>
              {formatCurrency(totalRevenue)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="#94a3b8" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                총 비용
              </Typography>
              <Typography variant="body1" fontWeight={600} color="#475569">
                {formatCurrency(totalExpense)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="#94a3b8" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                총 수익
              </Typography>
              <Typography variant="body1" fontWeight={600} color="#475569">
                {formatCurrency(totalProfit)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 주간 매출 차트: 모바일에서는 숨김 */}
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 3,
              p: 3,
              border: '1px solid #f1f5f9',
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              mb={2}
              textAlign="center"
              color="#475569"
            >
              일별 매출 추이
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="date"
                  tickFormatter={(value: string) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}K`} />
                <RechartsTooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelFormatter={(value: string) =>
                    new Date(value).toLocaleDateString('ko-KR')
                  }
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0044D2"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                  name="매출"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
}
